import {
  formAwaitsAnswer,
  type GetArtifactResp,
  type HeavyArtifactResponse,
  type UserResponseArtifact,
} from "@/types/artifacts.ts";
import type { ThreadEventItem } from "@/types/threadEvents.ts";

/**
 * A confirm-gated connector tool call is one decision spread over two
 * artifacts on the same message: the `mcpToolCall` / `externalToolCall` card
 * carries the server, tool and arguments — what is being decided — and a
 * `userResponse` form carries the Yes/No control, with `actions[]` that submit
 * it. Shown as two cards, the decision sits on the one that shows nothing of
 * what it decides, and the card that does show it has no button (BIG-8133).
 *
 * Pairing moves the form's actions onto the call and drops the form, so the
 * call renders as one card with the evidence and the buttons — the shape a
 * browser action already has. The actions are the form's own, so firing one
 * still submits the form, which is what records the approval and resumes the
 * agent. Nothing here changes what the server stores or sends.
 *
 * Once the form is answered the decision is made, but the call keeps its
 * server status until the agent's next run acts on the answer — seconds
 * normally, longer if that run fails. Shown raw, that is a card that says
 * "Waiting for approval" with nothing left to press. So an answered form pairs
 * too: the call reads as approved or cancelled and leaves the review queue,
 * and the server's own status takes over when it changes.
 */

const TOOL_CALL_TYPES: ReadonlySet<string> = new Set(["mcpToolCall", "externalToolCall"]);

type ApprovalForm = NonNullable<UserResponseArtifact["form"]>;
type ApprovalFormField = ApprovalForm["fields"][number];

/**
 * Set on a paired call: the artifact a decision on this card is submitted
 * against. The review stages decisions by the card's own id, but the server
 * applies an action to the artifact whose actions it re-derives, and the
 * approve/cancel actions are the form's — so the wire request names the form.
 */
export type DecisionTarget = { decisionArtifactId?: string };

/** The pairing target the artifact carries, if it was paired. */
function pairedTarget(artifact: GetArtifactResp) {
  if (!("decisionArtifactId" in artifact)) return undefined;
  const { decisionArtifactId } = artifact;
  return typeof decisionArtifactId === "string" ? decisionArtifactId : undefined;
}

/** The artifact a staged decision on this card is sent for: the card itself, unless paired. */
export function decisionArtifactId(artifact: GetArtifactResp) {
  return pairedTarget(artifact) ?? artifact.id;
}

function isPendingToolCall(artifact: GetArtifactResp): artifact is HeavyArtifactResponse {
  return (
    artifact.isHeavy &&
    TOOL_CALL_TYPES.has(artifact.artifactType) &&
    artifact.status === "waitingForUserAction"
  );
}

/** An unanswered form whose buttons can stand in for the call's. */
function isApprovalForm(artifact: GetArtifactResp): artifact is UserResponseArtifact {
  return (
    artifact.artifactType === "userResponse" &&
    formAwaitsAnswer(artifact.form) &&
    (artifact.actions?.length ?? 0) > 0
  );
}

/** A form the user has answered by submitting it (not one retired by a chat reply). */
function isAnsweredForm(artifact: GetArtifactResp): artifact is UserResponseArtifact {
  return artifact.artifactType === "userResponse" && artifact.form?.values !== undefined;
}

/**
 * What an answered approval form says about its call. The answer is the
 * chosen option's id — `approve` / `cancel` for every confirm-gated module —
 * so only `cancel` needs recognising. An answer matching no option (free text
 * typed into the answer box) is one the module will not act on; it reads as
 * answered rather than approved so the card doesn't promise a run.
 */
function answeredLabel(form: ApprovalForm) {
  const field = form.fields.find(
    (f: ApprovalFormField) => f.type === "select" || f.type === "selectHandle",
  );
  if (field === undefined || !("options" in field)) return "Answered";
  const answer = form.values?.[field.id];
  if (answer === undefined) return "Answered";
  const option = field.options.find((o) => o.value === answer);
  if (!option) return "Answered";
  return (option.id ?? option.value) === "cancel" ? "Cancelled" : "Approved";
}

/**
 * One message's artifacts with each pending tool call carrying its sibling
 * form's actions and the form removed. Pairs in order — the first pending call
 * takes the first unanswered form on the message — and a call with no form to
 * take stays as it came. Returns the input array itself when nothing pairs,
 * so memoised consumers see no change.
 */
export function pairToolCallApprovals(artifacts: GetArtifactResp[]) {
  if (!artifacts.some(isPendingToolCall)) return artifacts;
  const forms = artifacts.filter((a) => isApprovalForm(a) || isAnsweredForm(a));
  if (forms.length === 0) return artifacts;

  const taken = new Set<string>();
  const paired = artifacts.map((artifact): GetArtifactResp => {
    if (!isPendingToolCall(artifact)) return artifact;
    const form = forms.find((f) => f.messageId === artifact.messageId && !taken.has(f.id));
    if (!form) return artifact;
    taken.add(form.id);
    const call: HeavyArtifactResponse & DecisionTarget =
      form.form?.values !== undefined
      ? // Decided: no buttons, no biscuit (so no queue card), and a label that
        // says what was decided while the server status still says waiting.
        { ...artifact, biscuit: undefined, statusLabel: answeredLabel(form.form), decisionArtifactId: form.id }
      : { ...artifact, actions: form.actions, decisionArtifactId: form.id };
    return call;
  });
  if (taken.size === 0) return artifacts;
  return paired.filter((artifact) => !taken.has(artifact.id));
}

/**
 * A fresh copy of a paired call, carrying what the latest pairing gave it. A
 * detail fetch returns the artifact alone, without the sibling form that the
 * pairing read from, so a card that re-reads itself from that fetch would
 * fall back to a bare "waiting" call — or, worse, to an earlier pairing the
 * detail cache still holds, since answering the form doesn't touch the call's
 * own timestamp. The current pairing wins while the fresh copy is still
 * waiting; once the server has moved the call on, the fresh copy wins.
 */
export function carryPairedApproval(fresh: GetArtifactResp, paired: GetArtifactResp) {
  const target = pairedTarget(paired);
  if (!target || fresh === paired || fresh.id !== paired.id) return fresh;
  if (!isPendingToolCall(fresh) || !paired.isHeavy) return fresh;
  const call: HeavyArtifactResponse & DecisionTarget = {
    ...fresh,
    actions: paired.actions,
    biscuit: paired.biscuit,
    statusLabel: paired.statusLabel,
    decisionArtifactId: target,
  };
  return call;
}

/**
 * {@link pairToolCallApprovals} applied to every message in a feed. Items are
 * replaced only where a pairing happened, so an unchanged feed is returned as
 * the same array.
 */
export function pairToolCallApprovalsInFeed<T extends ThreadEventItem>(items: T[]) {
  let changed = false;
  const out = items.map((item): T => {
    if (item.type !== "message" || !item.message.artifacts) return item;
    const artifacts = pairToolCallApprovals(item.message.artifacts);
    if (artifacts === item.message.artifacts) return item;
    changed = true;
    return { ...item, message: { ...item.message, artifacts } };
  });
  return changed ? out : items;
}
